// import { LatLng, icon } from 'leaflet';
import moment from 'moment';

import { generateTimestampsInSeconds } from '@/util/date';
import {
  parseTrailResponse, trailRangeFrom, hourWindow, rollupPointDetail, mapGpsSeriesToTrail,
} from '@/util/trail/trailData';
import {
  deriveRpmTypesFromSummary, deriveAeCountFromSummary, deriveTempPositionsFromSummary,
  SERIES_REQUEST_PARAMS, DATALOG_SERIES_REQUEST_PARAMS,
} from '@/util/summary/seriesToSummaryData';
import { fetchSummarySeriesData } from '@/util/summary/summarySeries';
import { groupDeviceIdsByType, resolveDeniedSensorTypes } from '@/util/ship/pulseRequest';
import { getDeviceStatus, deriveDeviceStatusFromPulse, HEALTH_SENSOR_TYPES } from '@/util/ship/deviceStatus';
import { isSensorFresh, expectedHealthSensors, resolveHealthStatus } from '@/util/ship/shipStatus';
import { convertDecimalToDMS } from '@/util/coordinateFormat';

import AssetSvc from '@/services/AssetSvc';
import PulseSvc from '@/services/PulseSvc';
import SummarySvc from '@/services/SummarySvc';
import SeriesSvc from '@/services/SeriesSvc';
import DeviceSvc from '@/services/DeviceSvc';
// eslint-disable-next-line no-unused-vars
import LandmarkSvc from '@/services/LandmarkSvc';

import { buildIconConfig } from '@/util/ship/shipIcons';

import {
  getAllVisibilityStatuses,
  saveVisibilityStatus,
  saveAllVisibilityStatuses,
} from '@/services/dbService';

const state = {
  assets: [],
  isLiveTrackingActive: false,
};

// Ambang GPS masih dianggap segar. Sat lapor jarang, jadi tolerannya 6 jam.
const GPS_FRESH_SECONDS = 600;
const SAT_GPS_FRESH_SECONDS = 6 * 3600;
const gpsFreshLimit = (isSatMode) => (isSatMode ? SAT_GPS_FRESH_SECONDS : GPS_FRESH_SECONDS);

const processSummarySeriesData = (summary, firstQueryTimestamp, lastQueryTimestamp) => {
  // Bucket logic:
  // We generate a list of timestamps (buckets) between the requested start and end
  // using a fixed interval (3600 seconds = 1 hour). Server returns aggregated
  // data keyed by timestamp; we round those keys into the same hourly buckets
  // so each bucket represents data for that hour.
  const timestamps = generateTimestampsInSeconds(firstQueryTimestamp, lastQueryTimestamp, 3600);

  const roundedData = (data) => {
    const rounded = {};
    Object.keys(data || {}).forEach((key) => {
      // Round raw timestamps into the hourly bucket they belong to
      const roundedKey = Math.round(key / 3600) * 3600;
      rounded[roundedKey] = data[key];
    });
    return rounded;
  };

  const roundedGps = roundedData(summary.gps?.data);
  const roundedRpm = roundedData(summary.rpm?.data);
  const roundedFm = roundedData(summary.flowmeter?.data);
  const roundedAe = roundedData(summary.ae?.data);

  return timestamps.map((timestamp) => {
    const gpsData = roundedGps[timestamp] || {};
    const rpmData = roundedRpm[timestamp] || {};
    const fmData = roundedFm[timestamp] || {};
    const aeData = roundedAe[timestamp] || {};

    const latLong = [gpsData?.additional?.LAST_dlgpLatitude_4, gpsData?.additional?.LAST_dlgpLongitude_5];
    const totalCruise = gpsData?.additional?.SUM_dlgpDistance_3;
    const avgSpeed = gpsData.speed;
    const currentAe = Object.values(aeData).find((v) => v.rpm > 0)?.no + 1;
    const portRpm = rpmData.PORT?.rpm;
    const starboardRpm = rpmData.STARBOARD?.rpm;
    const centerRpm = rpmData.CENTER?.rpm;
    const portInFlowrate = fmData.PORT_Flow;
    const starboardInFlowrate = fmData.STARBOARD_Flow;

    return {
      gpsData,
      rpmData,
      fmData,
      aeData,

      dateTime: moment.unix(timestamp).format('DD-MM-YYYY HH:mm:ss'),
      timestamp,
      latLong,
      heading: gpsData.track,
      totalCruise,
      avgSpeed,
      currentAe,
      portRpm,
      starboardRpm,
      centerRpm,
      portInFlowrate,
      starboardInFlowrate,
    };
  });
};

const fetchGpsOnlyTrail = async (asset, firstQueryTimestamp, lastQueryTimestamp) => {
  const gpsDevice = (asset.devices || []).find((d) => d.devcType === 'gps');
  if (!gpsDevice) return { coordinates: [], coordinatesData: [] };

  const seriesResponse = await SeriesSvc.getAggregatedDataByPeriod('gps', firstQueryTimestamp, lastQueryTimestamp, {
    params: { device: [gpsDevice.devcUniqueId], aggregatedUnit: 'HOUR' },
  });
  const series = seriesResponse?.data?.data?.series || [];
  return mapGpsSeriesToTrail(series);
};

const mapGpsPulseToAssets = async (currAssets, massDevicesMap, gpsPulseDatas, currTime, mode = 'standard') => Promise.all(
  currAssets.map(async (asset) => {
    const newAsset = { ...asset };
    const assetDevices = massDevicesMap.get(asset.massId) || [];
    // Jangan reset pilihan user; default true cuma buat aset yang belum punya nilai.
    if (newAsset.isVisible === undefined) newAsset.isVisible = true;
    newAsset.locationInfo = null;

    // sat mode: Use massSateliteId to match GPS data
    const isSatMode = mode === 'sat';
    let gpsData;

    if (isSatMode) {
      // Match by massSateliteId
      gpsData = gpsPulseDatas.find((d) => d?.deviceId?.toLowerCase() === asset.massSateliteId?.toLowerCase());
    } else {
      // Standard mode: Match by device mapping
      const gpsDevice = assetDevices.find((v) => v.devcType === 'gps');
      if (gpsDevice) {
        gpsData = gpsPulseDatas.find((d) => d?.deviceId?.toLowerCase() === gpsDevice.devcUniqueId.toLowerCase());
      }
    }

    if (gpsData) {
      const gpsDiffTime = parseInt(currTime) - parseInt(gpsData.timestamp);
      newAsset.trail = false;
      newAsset.dotTrail = true;
      newAsset.lastGpsData = gpsData;
      newAsset.lastGpsDataPulse = gpsData;
      newAsset.lastCoordinate = `${convertDecimalToDMS(gpsData.latitude, true)}, ${convertDecimalToDMS(gpsData.longitude, false)}`;
      // eslint-disable-next-line no-undef
      newAsset.lastLatLng = L.latLng(gpsData.latitude, gpsData.longitude);
      newAsset.dataStatus = gpsDiffTime < gpsFreshLimit(isSatMode);
      newAsset.gpsDiffTime = gpsDiffTime; // Store GPS diffTime for later use

      // Determine ship status: online, offline, no-plc (GPS ok but no PLC data)
      // Default to online/offline based on GPS recency, will be updated later if no PLC
      const isGpsOnline = gpsDiffTime < gpsFreshLimit(isSatMode);
      newAsset.shipStatus = isGpsOnline ? 'online' : 'offline';

      const { iconConfig, markerIconUrl } = buildIconConfig(asset, newAsset.shipStatus);
      newAsset.iconConfig = iconConfig;
      newAsset.markerIconUrl = markerIconUrl;

      try {
        const locateResponse = await LandmarkSvc.landmarks.locate({
          latitude: gpsData.latitude,
          longitude: gpsData.longitude,
        });
        newAsset.locationInfo = locateResponse?.data?.data || null;
      } catch (error) {
        console.warn('Failed to fetch vessel location:', error);
      }
    } else {
      // No GPS data - set status to no-gps
      newAsset.shipStatus = 'no-gps';
      newAsset.dataStatus = false;

      const { iconConfig, markerIconUrl } = buildIconConfig(asset, 'no-gps');
      newAsset.iconConfig = iconConfig;
      newAsset.markerIconUrl = markerIconUrl;

      console.warn(`No GPS data found for asset: ${asset.massId}`);
    }
    return newAsset;
  }),
);

const mapOtherPulseToAssets = (
  assetsWithGps,
  massDevicesMap,
  aePulseDatas,
  rpmPulseDatas,
  fmPulseDatas,
  temperaturePulseDatas,
  powermeterPulseDatas,
  currTime,
  isSatMode = false,
  deniedSensorTypes = [],
) => assetsWithGps.map((asset) => {
  const newAsset = { ...asset };
  const assetDevices = massDevicesMap.get(asset.massId) || [];
  // PulseSvc.getPulse balikin null (bukan []) kalau ditolak izin (errorCode 412) -
  // normalize di sini biar .filter() di bawah gak error buat role dengan akses terbatas.
  aePulseDatas = aePulseDatas || [];
  rpmPulseDatas = rpmPulseDatas || [];
  fmPulseDatas = fmPulseDatas || [];
  temperaturePulseDatas = temperaturePulseDatas || [];
  powermeterPulseDatas = powermeterPulseDatas || [];

  // sat mode: Use massSateliteId to match AE data
  if (isSatMode && asset.massSateliteId) {
    const aeData = aePulseDatas.filter((d) => d.deviceId === asset.massSateliteId);
    if (aeData && aeData.length > 0) newAsset.lastAeData = aeData;
  } else {
    // Standard mode: Use device mapping
    const aeDevice = assetDevices.find((v) => v.devcType === 'ae');
    if (aeDevice) {
      const aeData = aePulseDatas.filter((d) => d.deviceId === aeDevice.devcUniqueId);
      if (aeData && aeData.length > 0) newAsset.lastAeData = aeData;
    }
  }

  const rpmDevice = assetDevices.find((v) => v.devcType === 'rpm');
  if (rpmDevice) {
    const rpmData = rpmPulseDatas.filter((d) => d.deviceId === rpmDevice.devcUniqueId);
    if (rpmData && rpmData.length > 0) {
      newAsset.lastRpmData = rpmData;
    }
  }

  const fmDeviceIds = assetDevices.filter((v) => v.devcType === 'flowmeter').map((v) => v.devcUniqueId);
  if (fmDeviceIds.length > 0) {
    const fmData = fmPulseDatas.filter((d) => fmDeviceIds.includes(d.deviceId));
    if (fmData && fmData.length > 0) newAsset.lastFmData = fmData;
  }

  const temperatureDeviceIds = assetDevices.filter((v) => v.devcType === 'temperature').map((v) => v.devcUniqueId);
  if (temperatureDeviceIds.length > 0) {
    const temperatureData = temperaturePulseDatas.filter((d) => temperatureDeviceIds.includes(d.deviceId));
    if (temperatureData && temperatureData.length > 0) newAsset.lastTemperatureData = temperatureData;
  }

  const powermeterDeviceIds = assetDevices.filter((v) => v.devcType === 'powermeter').map((v) => v.devcUniqueId);
  if (powermeterDeviceIds.length > 0) {
    const powermeterData = powermeterPulseDatas.filter((d) => powermeterDeviceIds.includes(d.deviceId));
    if (powermeterData && powermeterData.length > 0) newAsset.lastPowermeterData = powermeterData;
  }

  // --- Status kesehatan sensor (generalized) ---
  // Tahap GPS sudah menetapkan 'online'/'offline'. Di sini status ditimpa
  // berdasarkan sensor yang MEMANG dimiliki aset (rpm/flowmeter/ae/temperature):
  //  - GPS segar + >=1 sensor yang diharapkan hilang/basi -> no-sensor (oranye)
  //  - GPS basi  + >=1 sensor masih segar -> no-gps (masalah GPS diprioritaskan)
  // Aset tanpa sensor kesehatan sama sekali (mis. hanya GPS) tetap online.
  // Sensor yang ditolak izin (role gak boleh liat) dikeluarin dari daftar "diharapkan" -
  // itu bukan sensor mati, cuma emang gak boleh dicek statusnya oleh role ini.
  const expected = expectedHealthSensors(assetDevices.map((d) => d.devcType), deniedSensorTypes);
  const freshness = {
    rpm: isSensorFresh(newAsset.lastRpmData, currTime),
    flowmeter: isSensorFresh(newAsset.lastFmData, currTime),
    ae: isSensorFresh(newAsset.lastAeData, currTime),
    temperature: isSensorFresh(newAsset.lastTemperatureData, currTime),
  };

  const healthStatus = resolveHealthStatus({
    expected,
    freshness,
    isGpsOnline: newAsset.gpsDiffTime < gpsFreshLimit(isSatMode),
  });

  if (healthStatus) {
    newAsset.shipStatus = healthStatus;
    const { iconConfig, markerIconUrl } = buildIconConfig(newAsset, healthStatus);
    newAsset.iconConfig = iconConfig;
    newAsset.markerIconUrl = markerIconUrl;
  }

  return newAsset;
});

const mutations = {
  UPDATE_ASSET_VISIBILITY(state, { massId, isVisible }) {
    const asset = state.assets.find((asset) => asset.massId === massId);
    if (asset) {
      asset.isVisible = isVisible;
    }
  },
  SET_ALL_ASSETS_VISIBILITY(state, isVisible) {
    state.assets.forEach((asset) => {
      asset.isVisible = isVisible;
    });
  },
  SET_ASSETS(state, assets) {
    state.assets = assets;
  },
  SET_LIVE_TRACKING_ACTIVE(state, status) {
    state.isLiveTrackingActive = status;
  },
};

// Epoch per jenis fetch. Sengaja di scope modul, bukan di state: di Vuex action
// gak ada `this`, dan kalo ditaruh di state tiap kenaikan jadi mutasi reaktif.
const summaryEpoch = { value: 0 };

// Rentang terakhir yang diminta per massId. Dipasang SEBELUM await; hasil yang
// rentangnya udah bukan yang terakhir dibuang, bukan di-commit.
const pendingSummaryRange = {};
const pendingTrailRange = {};

const lastDataEpoch = { value: 0 };
const reconcileEpoch = { value: 0 };

// Naik dan tangkap sekaligus; jangan dipecah dua baris.
const nextEpoch = (counter) => {
  counter.value += 1;
  return counter.value;
};

const actions = {
  async getAssetsData({ commit }) {
    const res = await AssetSvc.getAssets();
    const assetsData = res?.data?.data ?? [];

    const processedAssets = assetsData.map((asset) => {
      const newAsset = { ...asset };
      if (newAsset.massDevice) {
        newAsset.devices = newAsset.massDevice;
        delete newAsset.massDevice;
      } else {
        newAsset.devices = [];
      }
      return newAsset;
    });

    commit('SET_ASSETS', processedAssets);
    return processedAssets;
  },

  async getAssetsDevices({ commit, state }) {
    const currAssets = Object.values(state.assets);

    const finalAssetsPromises = currAssets.map(async (asset) => {
      let deviceData = asset.devices || [];

      // If devices are missing, fetch them ONCE.
      if (deviceData.length === 0) {
        try {
          const param = { findField: 'devcMassId', findValue: asset.massId, max: 100 };
          const response = await DeviceSvc.getDevicesByAssetId(param);
          deviceData = response?.data?.data || [];
        } catch (error) {
          console.error(`Failed to fetch devices for asset ${asset.massId}`, error);
          deviceData = []; // Ensure it's an array on failure
        }
      }

      // Now, with the final deviceData (either from master or fetched), calculate status.
      const deviceStatus = getDeviceStatus(deviceData);

      return {
        ...asset,
        devices: deviceData, // Update with fetched data if it was missing
        deviceStatus,
      };
    });

    const finalAssets = await Promise.all(finalAssetsPromises);

    commit('SET_ASSETS', finalAssets);
    // console.log('[asset.js] Devices processed and status calculated:', finalAssets);
    return finalAssets;
  },

  stopLiveTracking({ commit }) {
    commit('SET_LIVE_TRACKING_ACTIVE', false);
  },

  async getLastData({ commit, state, dispatch }, mode = 'standard') {
    dispatch('stopLiveTracking');
    commit('SET_LIVE_TRACKING_ACTIVE', true);
    // Penanda dipasang sebelum await pertama. Flag isLiveTrackingActive gak bisa
    // dipakai buat ini: tiap panggilan baru nyalain lagi.
    const epoch = nextEpoch(lastDataEpoch);

    const currTime = Math.floor(Date.now() / 1000);
    const currAssets = Object.values(state.assets);
    // Fetching last data for assets (debug log removed)
    // console.log('[getLastData] Current assets:', JSON.stringify(currAssets, null, 2));

    const isSatMode = mode === 'sat';

    // --- sat Mode: Only fetch GPS and RPM using massSateliteId ---
    if (isSatMode) {
      // Get satellite IDs from assets
      const satelliteGpsIds = currAssets
        .filter((asset) => asset.massSateliteId)
        .map((asset) => asset.massSateliteId);

      const satelliteRpmIds = currAssets
        .filter((asset) => asset.massSateliteId)
        .map((asset) => asset.massSateliteId);

      // Fetch GPS data
      const gpsPulseDatas = (satelliteGpsIds.length > 0
        ? await PulseSvc.getPulse(satelliteGpsIds, 'gps', 'sat')
        : []) || []; // null (ditolak izin) -> [] biar gak error di mapGpsPulseToAssets

      // Fetch RPM data
      const rpmPulseDatas = (satelliteRpmIds.length > 0
        ? await PulseSvc.getPulse(satelliteRpmIds, 'rpm', 'sat')
        : []) || [];

      // Fetch AE data
      const aePulseDatas = (satelliteRpmIds.length > 0
        ? await PulseSvc.getPulse(satelliteRpmIds, 'ae', 'sat')
        : []) || [];

      // Map GPS data to assets (pass 'sat' mode)
      const assetsWithGps = await mapGpsPulseToAssets(currAssets, new Map(), gpsPulseDatas, currTime, 'sat');

      // Urutan argumen ikut tanda tangan: AE dulu, baru RPM.
      const finalMappedAssets = mapOtherPulseToAssets(
        assetsWithGps,
        new Map(),
        aePulseDatas,
        rpmPulseDatas,
        [], // fmPulseDatas
        [], // temperaturePulseDatas
        [], // powermeterPulseDatas
        currTime,
        true, // isSatMode
      );

      if (epoch !== lastDataEpoch.value) return;
      commit('SET_ASSETS', deriveDeviceStatusFromPulse(finalMappedAssets));

      return;
    }

    // --- Standard Mode: Full device mapping and all data types ---
    const massDevicesMap = new Map();
    currAssets.forEach((asset) => {
      massDevicesMap.set(asset.massId, asset.devices || []);
    });

    // --- Stage 1: GPS ---
    const gpsIds = currAssets.flatMap((asset) => (asset.devices || []).filter((d) => d.devcType === 'gps').map((d) => d.devcUniqueId));
    const gpsPulseDatas = (gpsIds.length > 0 ? await PulseSvc.getPulse(gpsIds, 'gps', mode) : []) || [];

    const assetsWithGps = await mapGpsPulseToAssets(currAssets, massDevicesMap, gpsPulseDatas, currTime, mode);

    if (epoch !== lastDataEpoch.value) return;
    commit('SET_ASSETS', assetsWithGps);

    if (!state.isLiveTrackingActive) return;

    // --- Stage 2: Other Devices ---
    const pulseGroups = groupDeviceIdsByType(currAssets);
    const pulseResults = await Promise.all(
      pulseGroups.map((group) => PulseSvc.getPulse(group.ids, group.type)),
    );

    const pulseDataMap = new Map();
    pulseGroups.forEach((group, index) => {
      pulseDataMap.set(group.type, pulseResults[index]);
    });

    const deniedSensorTypes = resolveDeniedSensorTypes(pulseDataMap, HEALTH_SENSOR_TYPES);

    const aePulseDatas = pulseDataMap.get('ae') || [];
    const rpmPulseDatas = pulseDataMap.get('rpm') || [];
    const fmPulseDatas = pulseDataMap.get('flowmeter') || [];
    const temperaturePulseDatas = pulseDataMap.get('temperature') || [];
    const powermeterPulseDatas = pulseDataMap.get('powermeter') || [];

    const finalMappedAssets = mapOtherPulseToAssets(
      assetsWithGps,
      massDevicesMap,
      aePulseDatas,
      rpmPulseDatas,
      fmPulseDatas,
      temperaturePulseDatas,
      powermeterPulseDatas,
      currTime,
      false, // isSatMode
      deniedSensorTypes,
    );

    if (epoch !== lastDataEpoch.value) return;
    commit('SET_ASSETS', deriveDeviceStatusFromPulse(finalMappedAssets));
  },

  async fetchSummary({ commit, state }, {
    start, end, interval, massId,
  }) {
    const assets = Object.values(state.assets);
    const epoch = nextEpoch(summaryEpoch);

    const firstQueryTimestamp = moment(start).unix();
    const lastQueryTimestamp = moment(end).unix();

    const currentAsset = assets.find((asset) => {
      const match = String(asset.massId) === String(massId);
      return match;
    });

    if (!currentAsset) {
      console.error(`[fetchSummary] Asset with massId ${massId} not found.`);
      return assets;
    }

    let existingDevcTypes = [];
    if (currentAsset.devices && currentAsset.devices.length > 0) {
      const uniqueTypes = new Set(currentAsset.devices.map((d) => d.devcType));
      existingDevcTypes = Array.from(uniqueTypes);
    }

    existingDevcTypes = existingDevcTypes.filter((type) => type !== 'gateway' && type !== 'limitswitch' && type !== 'power');

    if (existingDevcTypes.length === 0) {
      console.warn(`[fetchSummary] No devices found on asset ${massId}. Summary fetch skipped.`);
      return assets;
    }

    const aggregatedUnit = interval.toUpperCase();

    // Satu request untuk seluruh rentang dengan semua device type sekaligus —
    // backend meng-cache per hari, mecah rentang malah lambat.
    const response = await SummarySvc.getSummary({
      start: firstQueryTimestamp,
      end: lastQueryTimestamp,
      aggregatedUnit,
      devcType: existingDevcTypes,
      devcMassId: massId,
      showDetails: true,
    });

    const summaryData = response?.data?.data || {};

    // Hasil yang telat gak boleh nimpa yang baru.
    if (epoch !== summaryEpoch.value) return Object.values(state.assets);

    const mappedAssets = Object.values(state.assets).map((asset) => {
      if (String(asset.massId) === String(massId)) {
        const mappedCoordinatesData = processSummarySeriesData(summaryData, firstQueryTimestamp, lastQueryTimestamp);

        // Koordinat sekarang dipegang fetchShipTrail biar polyline cuma punya
        // satu penulis. Datanya gak dibuang — dituang ke cache titik dot trail,
        // jadi klik dot di halaman ini gak perlu request lagi.
        const pointDetail = {};
        mappedCoordinatesData.forEach((p) => {
          if (p.timestamp) pointDetail[p.timestamp] = p;
        });
        const updatedAsset = {
          ...asset,
          summary: summaryData,
          // Penanda rentang yang sama kayak fetchShipSummary, biar
          // ensureShipSummary gak narik rollup lagi di halaman yang
          // summary-nya udah lengkap.
          summaryRangeKey: `${firstQueryTimestamp}-${lastQueryTimestamp}`,
          trailPointDetail: { ...(asset.trailPointDetail || {}), ...pointDetail },
        };

        // Bucket paling akhir itu jam berjalan, dan backend belum ngagregasi jam
        // itu — jadi hampir selalu kosong buat rentang yang ujungnya "sekarang".
        // Ambil bucket terisi terakhir, bukan bucket terakhir. Kalau enggak,
        // lastLatLng gak keisi -> createShipMarker balik null -> addShipsToMap
        // kena `if (!marker) return` -> marker DAN trail dua-duanya gak kegambar.
        const lastData = [...mappedCoordinatesData]
          .reverse()
          .find((d) => d?.gpsData?.additional?.LAST_dlgpLatitude_4
            && d?.gpsData?.additional?.LAST_dlgpLongitude_5);
        if (lastData?.gpsData?.additional?.LAST_dlgpLatitude_4 && lastData?.gpsData?.additional?.LAST_dlgpLongitude_5) {
          updatedAsset.lastGpsData = lastData.gpsData;
          // eslint-disable-next-line no-undef
          updatedAsset.lastLatLng = L.latLng(lastData.gpsData?.additional?.LAST_dlgpLatitude_4, lastData.gpsData?.additional?.LAST_dlgpLongitude_5);
        }
        return updatedAsset;
      }
      return asset;
    });

    commit('SET_ASSETS', mappedAssets);
    return mappedAssets;
  },

  // Summary dari endpoint rollup: cuma blok agregat (~5 KB), nol deret waktu.
  // `fetchSummary` di atas masih ada tapi nol halaman yang manggil; defaultnya
  // udah 'rollup'.
  async fetchSummaryRollup({ commit, state }, payload) {
    const { massId, start, end } = payload || {};
    const asset = Object.values(state.assets).find((a) => String(a.massId) === String(massId));
    if (!asset) {
      console.error(`[fetchSummaryRollup] Asset with massId ${massId} not found.`);
      return;
    }

    const startUnix = moment(start).unix();
    const endUnix = moment(end).unix();
    const rangeKey = `${startUnix}-${endUnix}`;
    pendingSummaryRange[massId] = rangeKey;

    // Aturan sama kayak fetchSummary legacy, plus powermeter dikecualiin (rollup
    // belum ngelayanin, jalurnya sendiri di bawah). JANGAN di-hardcode: hardcode
    // ['gps','rpm','flowmeter','ae'] bikin blok temperature gak ikut balik dan
    // baris suhu di Report ilang diem-diem.
    const excluded = ['gateway', 'limitswitch', 'power', 'powermeter'];
    const devcType = Array.from(new Set((asset.devices || []).map((d) => d.devcType)))
      .filter((type) => !excluded.includes(type));

    if (devcType.length === 0) {
      console.warn(`[fetchSummaryRollup] No devices found on asset ${massId}. Summary fetch skipped.`);
      return;
    }

    const res = await SummarySvc.getAssetSummaryRollup({
      devcMassId: asset.massId, devcType, start: startUnix, end: endUnix,
    });
    const body = res?.data?.data || {};

    // Tipe di sini GAK ADA di body, dan itu beda dari nol.
    const unavailable = res?.data?.meta?.unavailable || [];
    if (unavailable.length > 0) {
      console.warn(`[fetchSummaryRollup] rollup belum ngelayanin: ${unavailable.join(', ')} — jangan dianggap nol`);
    }

    // powermeter belum dilayani rollup tapi FmsReport masih baca bloknya.
    // Endpoint lama dijadwalkan mati: gagal = blok kosong, bukan halaman rusak.
    if ((asset.devices || []).some((d) => d.devcType === 'powermeter')) {
      try {
        const pm = await SummarySvc.getSummary({
          devcType: ['powermeter'],
          devcMassId: asset.massId,
          start: startUnix,
          end: endUnix,
          aggregatedUnit: 'HOUR',
          aggregatedLength: 1,
          showDetails: false,
        });
        if (pm?.data?.data?.powermeter) body.powermeter = pm.data.data.powermeter;
      } catch (e) {
        console.warn('[fetchSummaryRollup] powermeter gagal ditarik dari endpoint lama, bloknya dikosongin', e);
      }
    }

    // baca ulang state setelah await biar gak nimpa hasil fetch paralel lain
    const updated = Object.values(state.assets).map((a) => (String(a.massId) !== String(massId) ? a : {
      ...a, summary: body, summaryRangeKey: rangeKey, summarySource: 'rollup',
    }));
    if (pendingSummaryRange[massId] !== rangeKey) return;
    commit('SET_ASSETS', updated);
  },

  // Deret waktu buat chart, ditempel ke summary yang udah ditarik
  // fetchSummaryRollup. Balikin daftar tipe yang gagal biar halaman bisa
  // nampilin toast — store gak nyentuh UI.
  async fetchSummarySeries({ commit, state }, payload) {
    const {
      massId, start, end, interval, seriesVariant, seriesTypes,
    } = payload || {};
    const asset = Object.values(state.assets).find((a) => String(a.massId) === String(massId));
    if (!asset?.summary) return [];

    const startUnix = moment(start).unix();
    const endUnix = moment(end).unix();
    const rangeKey = `${startUnix}-${endUnix}`;

    const { data, failedTypes } = await fetchSummarySeriesData({
      devices: asset.devices || [],
      start: startUnix,
      end: endUnix,
      interval,
      rpmTypes: deriveRpmTypesFromSummary(asset.summary),
      aeCount: deriveAeCountFromSummary(asset.summary),
      tempPositions: deriveTempPositionsFromSummary(asset.summary),
      requestParams: seriesVariant === 'datalog'
        ? DATALOG_SERIES_REQUEST_PARAMS
        : SERIES_REQUEST_PARAMS,
      seriesTypes,
    });

    // baca ulang state setelah await: kalau user keburu ganti aset/rentang,
    // hasil yang telat DIBUANG, bukan nimpa yang baru.
    const fresh = Object.values(state.assets).find((a) => String(a.massId) === String(massId));
    if (!fresh?.summary || fresh.summaryRangeKey !== rangeKey) return failedTypes;

    // Nyusun objek gabungan lalu commit SEKALI. Nambah key `data` ke objek yang
    // udah reaktif gak kebaca Vue 2 — ini udah kena sekali.
    const merged = {};
    Object.keys(fresh.summary).forEach((type) => {
      merged[type] = data[type]
        ? { ...fresh.summary[type], data: data[type] }
        : fresh.summary[type];
    });

    const updated = Object.values(state.assets).map((a) => (String(a.massId) !== String(massId) ? a : {
      ...a, summary: merged,
    }));
    commit('SET_ASSETS', updated);
    return failedTypes;
  },

  // Geometry trail doang — /summary/trail ~56x lebih kecil per titik daripada
  // /summary/aggregated/period. Stats-nya nyusul lewat fetchShipSummary.
  async fetchShipTrail({ commit, state, rootGetters }, payload) {
    const {
      massId, days = 1, start, end,
    } = payload || {};
    const asset = Object.values(state.assets).find((a) => String(a.massId) === String(massId));
    if (!asset) return;

    const range = trailRangeFrom({
      start, end, days, lastTimestamp: asset.lastGpsData?.timestamp,
    });
    if (!range) return;

    const rangeKey = `${range.start}-${range.end}`;
    pendingTrailRange[massId] = rangeKey;

    let trail;
    if (rootGetters['auth/isGpsViewer']) {
      trail = await fetchGpsOnlyTrail(asset, range.start, range.end);
    } else {
      const res = await SummarySvc.getTrail({
        devcMassId: asset.massId, start: range.start, end: range.end,
      });
      trail = parseTrailResponse(res?.data?.data);
    }

    // baca ulang state setelah await biar gak nimpa hasil fetch paralel lain
    const updated = Object.values(state.assets).map((a) => (String(a.massId) !== String(massId) ? a : {
      ...a, coordinates: trail.coordinates, coordinatesData: trail.coordinatesData,
    }));
    if (pendingTrailRange[massId] !== rangeKey) return;
    commit('SET_ASSETS', updated);
  },

  // Trail stats doang (9 field skalar yang dibaca buildTrailStats). Endpoint
  // rollup gak bawa blok `data`, jadi jauh lebih cepat dari getSummary.
  // Catatan: channel PORT/STARBOARD di rollup kemungkinan ketuker —
  // lihat spec 2026-08-29, keputusan user: pakai apa adanya.
  async fetchShipSummary({ commit, state }, payload) {
    const {
      massId, days = 1, start, end,
    } = payload || {};
    const asset = Object.values(state.assets).find((a) => String(a.massId) === String(massId));
    if (!asset) return;

    const range = trailRangeFrom({
      start, end, days, lastTimestamp: asset.lastGpsData?.timestamp,
    });
    if (!range) return;

    const rangeKey = `${range.start}-${range.end}`;
    if (asset.summaryRangeKey === rangeKey) return;
    pendingSummaryRange[massId] = rangeKey;

    const res = await SummarySvc.getAssetSummaryRollup({
      devcMassId: asset.massId,
      devcType: ['gps', 'rpm', 'flowmeter', 'ae'],
      start: range.start,
      end: range.end,
    });

    // baca ulang state setelah await biar gak nimpa hasil fetch paralel lain
    const updated = Object.values(state.assets).map((a) => (String(a.massId) !== String(massId) ? a : {
      ...a, summary: res?.data?.data || {}, summaryRangeKey: rangeKey,
    }));
    if (pendingSummaryRange[massId] !== rangeKey) return;
    commit('SET_ASSETS', updated);
  },

  // Detail satu titik dot trail. Satu titik = satu rentang sejam, dan endpoint
  // rollup melayani rentang itu — jadi jalur map lepas total dari
  // /summary/aggregated/period yang bakal dibuang backend.
  async fetchTrailPointDetail({ state }, payload) {
    const { massId, timestamp } = payload || {};
    const asset = Object.values(state.assets).find((a) => String(a.massId) === String(massId));
    if (!asset || !timestamp) return undefined;
    if (asset.trailPointDetail?.[timestamp]) return asset.trailPointDetail[timestamp];

    const { start, end } = hourWindow(timestamp);
    const res = await SummarySvc.getAssetSummaryRollup({
      devcMassId: asset.massId,
      devcType: ['gps', 'rpm', 'flowmeter', 'ae'],
      start,
      end,
    });

    // gak commit ke store lagi - hasilnya cuma cache tampilan, dipegang MapService
    // biar SET_ASSETS gak trigger rebuild map & nutup popup dot yang lagi kebuka
    return rollupPointDetail(res?.data?.data || {});
  },

  async getAssetsFromAPI({ dispatch, state }) {
    dispatch('stopLiveTracking');

    await dispatch('getAssetsData');
    await dispatch('getAssetsDevices');
    await dispatch('getLastData');

    const initialAssets = state.assets.filter((asset) => asset.lastGpsData);
    return initialAssets;
  },

  async fetchAndReconcileAssets({ dispatch, commit }) {
    const epoch = nextEpoch(reconcileEpoch);
    const [apiAssets, visibilityMap] = await Promise.all([
      dispatch('getAssetsFromAPI'),
      getAllVisibilityStatuses(),
    ]);

    const reconciledAssets = apiAssets.map((asset) => {
      if (visibilityMap.has(asset.massId)) {
        asset.isVisible = visibilityMap.get(asset.massId);
      } else {
        asset.isVisible = true;
      }
      return asset;
    });

    if (epoch !== reconcileEpoch.value) return apiAssets;
    commit('SET_ASSETS', reconciledAssets);

    const allStatuses = reconciledAssets.map(({ massId, isVisible }) => ({ massId, isVisible }));
    await saveAllVisibilityStatuses(allStatuses);

    return reconciledAssets;
  },

  async updateAssetVisibility({ commit }, payload) {
    commit('UPDATE_ASSET_VISIBILITY', payload);
    await saveVisibilityStatus(payload.massId, payload.isVisible);
  },

  async setAllAssetsVisibility({ commit, state }, isVisible) {
    commit('SET_ALL_ASSETS_VISIBILITY', isVisible);
    const allStatuses = state.assets.map((asset) => ({
      massId: asset.massId,
      isVisible,
    }));
    await saveAllVisibilityStatuses(allStatuses);
  },

  // Recompute every asset's icon config from the current global icon settings
  // (style + size) and drop cached Leaflet icons so maps rebuild them.
  applyIconSettings({ commit, state }) {
    const assets = state.assets.map((asset) => {
      if (!asset.shipStatus) return asset;
      const { iconConfig, markerIconUrl } = buildIconConfig(asset, asset.shipStatus);
      return {
        ...asset,
        iconConfig,
        markerIconUrl,
        icon: null,
        markerIcon: null,
        headingIcon: null,
      };
    });
    commit('SET_ASSETS', assets);
  },

  resetAssetsData({ dispatch, commit }) {
    dispatch('stopLiveTracking');
    commit('SET_ASSETS', []);
    return [];
  },
};

const getters = {
  assets: (state) => state.assets,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
